import { computed, Injectable, signal } from '@angular/core';

import type { Maybe } from '../../types/base/';
import { icons } from '../../types/icon-list.type';
import { Toast, ToastTypes } from '../../types/toast.type';

type ToastTimer = {
  timeoutId: ReturnType<typeof setTimeout>;
  startedAt: number;
  remaining: number;
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  #uniqId = 0;
  #queue = signal<Toast[]>([]);
  #timers = new Map<string, ToastTimer>();
  current = computed(() => this.#queue().at(-1));
  queue = this.#queue.asReadonly();
  getById(idToFind: string): Maybe<Toast> {
    return this.#queue().find(({ id }) => id === idToFind);
  }

  add(payload: Partial<Toast> & { message: string }): Toast {
    const duration = this.#calculateToastDuration(payload.message, payload.duration);
    const toast = {
      id: payload.id || this.#getUniqId('toast'),
      type: ToastTypes.INFO,
      duration,
      ...payload,
    };

    this.#queue.update((queue) => [...queue, toast]);

    if (toast.duration > 0) {
      this.#scheduleRemove(toast.id, toast.duration);
    }

    return toast;
  }

  #calculateToastDuration(message: string, providedDuration?: number): number {
    if (providedDuration !== undefined) {
      // Clamp provided duration between 4s and 10s
      return Math.min(Math.max(providedDuration, 4000), 10000);
    }

    // Dynamic duration: 500ms per 10 characters, clamped to 4-10s
    const charCount = message?.trim().length || 1;
    const dynamicMs = Math.ceil(charCount / 10) * 500;
    return Math.min(Math.max(dynamicMs, 4000), 10000);
  }

  success(message: string, options?: Partial<Toast>): Toast {
    return this.add({
      icon: icons.CONFIRM_FILL,
      type: ToastTypes.SUCCESS,
      message,
      ...options,
    });
  }

  info(message: string, options?: Partial<Toast>): Toast {
    return this.add({
      icon: icons.INFO_FILL,
      type: ToastTypes.INFO,
      message,
      ...options,
    });
  }

  error(message: string, options?: Partial<Toast>): Toast {
    return this.add({
      icon: icons.ALERT_FILL,
      type: ToastTypes.ERROR,
      message,
      ...options,
    });
  }

  warning(message: string, options?: Partial<Toast>): Toast {
    return this.add({
      icon: icons.ALERT_FILL,
      type: ToastTypes.WARNING,
      message,
      ...options,
    });
  }

  remove(id: string): void {
    const timer = this.#timers.get(id);
    if (timer) clearTimeout(timer.timeoutId);
    this.#timers.delete(id);
    this.#queue.update((queue) => queue.filter((toast) => id !== toast.id));
  }

  pause(id: string): void {
    const timer = this.#timers.get(id);
    if (!timer) return;

    clearTimeout(timer.timeoutId);

    const elapsed = Date.now() - timer.startedAt;
    const remaining = Math.max(timer.remaining - elapsed, 0);

    this.#timers.set(id, { ...timer, remaining });
  }

  resume(id: string): void {
    const timer = this.#timers.get(id);
    if (!timer) return;

    if (timer.remaining <= 0) {
      this.#timers.delete(id);
      this.remove(id);

      return;
    }

    this.#scheduleRemove(id, timer.remaining);
  }

  #scheduleRemove(id: string, duration: number): void {
    const existing = this.#timers.get(id);
    if (existing) clearTimeout(existing.timeoutId);

    const startedAt = Date.now();
    const timeoutId = setTimeout(() => {
      this.#timers.delete(id);
      this.remove(id);
    }, duration);

    this.#timers.set(id, { timeoutId, startedAt, remaining: duration });
  }

  #getUniqId(prefix = 'toast'): string {
    return `${prefix}-${++this.#uniqId}`;
  }
}
